import {
  FlowStepEditorModel,
  FIXED_FINAL_STATUS_SLUG,
  statusSlugOf
} from '../models/flow-step-editor.model';

export { datePlusDays, toDateInput } from './status-flow-dates';
export {
  FlowStepEditorModel,
  FlowStepPayload,
  FIXED_FINAL_STATUS_SLUG,
  bindApprovedStatus,
  ensureApprovedFinalStep,
  isFixedFinalIndex,
  isFixedFinalStep,
  isTerminalStepIndex,
  nextStepSequence,
  statusSlugOf
} from '../models/flow-step-editor.model';

/** Busca proceso por slug, uuid o id. */
export function findProcessByKey(processes: any[], processKey: string): any | null {
  const key = String(processKey || '').trim();
  if (!key) return null;
  const upper = key.toUpperCase();
  return processes.find((p: any) =>
    String(p.slug || '').toUpperCase() === upper ||
    String(p.uuid || '') === key ||
    String(p.id || '') === key
  ) ?? null;
}

export type FlowValidationError =
  | 'NO_STATUS'
  | 'TOKEN_INCOMPLETE'
  | 'TOKEN_ROLE'
  | 'DOC_MISSING'
  | 'OBSERVE_REVIEWER'
  | 'OBSERVE_RESUBMIT'
  | 'FINAL_NOT_APPROVED'
  | null;

/** Valida pasos editables antes de armar payload. */
export function validateEditorFlows(
  flows: FlowStepEditorModel[],
  isTerminal: (i: number) => boolean
): FlowValidationError {
  if (flows.some((f) => !f.statusUuid && !f.statusId)) return 'NO_STATUS';
  if (flows.length && statusSlugOf(flows[flows.length - 1]) !== FIXED_FINAL_STATUS_SLUG) {
    return 'FINAL_NOT_APPROVED';
  }
  if (hasIncompleteToken(flows)) return 'TOKEN_INCOMPLETE';
  if (hasTokenRoleWithoutRequired(flows)) return 'TOKEN_ROLE';
  if (hasMissingDocument(flows, isTerminal)) return 'DOC_MISSING';
  if (hasObserveWithoutReviewer(flows, isTerminal)) return 'OBSERVE_REVIEWER';
  if (hasObserveWithoutResubmit(flows, isTerminal)) return 'OBSERVE_RESUBMIT';
  return null;
}

function hasIncompleteToken(flows: FlowStepEditorModel[]): boolean {
  return flows.some((f) => f.allowsToken && (!f.tokenAudience || !f.tokenConfirmStatusSlug));
}

function hasTokenRoleWithoutRequired(flows: FlowStepEditorModel[]): boolean {
  return flows.some((f) => f.allowsToken && f.tokenAudience === 'ROLE' && !f.requiredRole);
}

function hasMissingDocument(
  flows: FlowStepEditorModel[],
  isTerminal: (i: number) => boolean
): boolean {
  return flows.some((f, i) => !isTerminal(i) && f.requiresDocument && !f.documentTypeSlug);
}

function hasObserveWithoutReviewer(
  flows: FlowStepEditorModel[],
  isTerminal: (i: number) => boolean
): boolean {
  return false;
}

function hasObserveWithoutResubmit(
  flows: FlowStepEditorModel[],
  isTerminal: (i: number) => boolean
): boolean {
  return flows.some((f, i) => !isTerminal(i) && !!f.contentReviewRole && !f.resubmitToStatusSlug);
}

export function validationMessage(code: FlowValidationError): string {
  if (code === 'NO_STATUS') return 'Hay pasos sin estado configurado.';
  if (code === 'FINAL_NOT_APPROVED') {
    return 'El último paso del flujo debe ser Aprobada (APPROVED).';
  }
  if (code === 'TOKEN_INCOMPLETE') return 'Hay enlaces públicos sin audiencia o estado al confirmar.';
  if (code === 'TOKEN_ROLE') return 'El enlace público a rol requiere un rol en el paso.';
  if (code === 'DOC_MISSING') return 'Hay pasos con documento exigido sin tipo de documento.';
  if (code === 'OBSERVE_REVIEWER') return 'Hay pasos con observar activo sin rol revisor.';
  if (code === 'OBSERVE_RESUBMIT') {
    return 'Hay pasos con rol revisor sin destino al subsanar (Observar).';
  }
  return '';
}

/** Opciones Swal para guardar flujo. */
export function saveFlowSwalOptions() {
  return {
    title: '¿Cómo guardar el flujo?',
    html: `
      <p class="text-start small mb-2">Se guardará la configuración del proceso.</p>
      <ul class="text-start small mb-0">
        <li><b>Solo nuevas</b>: las solicitudes abiertas mantienen su snapshot.</li>
        <li><b>Aplicar a abiertas</b>: reescribe su snapshot; si falta un paso en el historial, quedan pendientes en ese paso.</li>
      </ul>
    `,
    icon: 'question' as const,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Solo nuevas',
    denyButtonText: 'Aplicar a abiertas',
    cancelButtonText: 'Cancelar'
  };
}

/** Opciones Swal al salir con cambios. */
export function discardChangesSwalOptions() {
  return {
    title: 'Cambios sin guardar',
    text: 'Tiene modificaciones en el flujo. Si sale, se perderán.',
    icon: 'warning' as const,
    showCancelButton: true,
    confirmButtonText: 'Salir sin guardar',
    cancelButtonText: 'Quedarme'
  };
}

export function saveApplyOpenSuccessText(affectedCount: number): string {
  return `Catálogo actualizado. Snapshot aplicado a ${affectedCount} solicitud(es) abierta(s).`;
}

export function extractSaveError(err: any): string {
  return err?.error?.details?.[0]?.message
    || err?.error?.error
    || err?.error?.message
    || err?.message
    || 'Error interno';
}

/** Roles de fallback si el catálogo aún no cargó. */
export const FALLBACK_ROLES = [
  { slug: 'MINISTRO_FE', name: 'MINISTRO_FE', displayName: 'Ministro de Fe', description: 'Ministro de Fe' },
  { slug: 'SEC_UCH', name: 'SEC_UCH', displayName: 'Central (Sec. UCH)', description: 'Central (Sec. UCH)' },
  { slug: 'ENCARGADO', name: 'ENCARGADO', displayName: 'Encargado', description: 'Encargado' }
];
